import React, { CSSProperties, useRef, useState } from 'react';

import {
  Accordion,
  accordionClasses,
  AccordionDetails,
  accordionDetailsClasses,
  AccordionSummary,
  accordionSummaryClasses,
  buttonBaseClasses,
  Grid2 as Grid,
  inputBaseClasses,
  Tab as MuiTab,
  Tabs as MuiTabs,
  paperClasses,
  styled,
  tabClasses,
  tabsClasses,
  tabScrollButtonClasses,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import ResponsiveGrid from '../../layout/grid';
import TextSelect from '../inputs/text-select';
import { useSectionsHeightAnimation } from '../tabbed-slots/use-sections-height-animation';
import { EndIcon, StartIcon } from './icons';
import { useTabGlider } from './use-tab-glider';

export const Tabs = ({
  sections,
  className = '',
  backgroundColor = 'var(--color-white)',
  showTabsOnMobile = false,
  autoScrollOffset = 0,
  paddingBetweenTitelAndContent,
  initialIndex = 0,
  disableAutoScroll = false,
  showDropdownOnMobile = false,
  alignTabs = 'center',
}: {
  sections: {
    title: string;
    content: React.ReactElement | React.ReactElement[] | null;
  }[];
  className?: string;
  backgroundColor?: CSSProperties['backgroundColor'];
  showTabsOnMobile?: boolean;
  autoScrollOffset?: number;
  paddingBetweenTitelAndContent?: string;
  initialIndex?: number;
  disableAutoScroll?: boolean;
  showDropdownOnMobile?: boolean;
  alignTabs?: 'left' | 'center';
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(initialIndex);
  const tabTitles = useRef<HTMLDivElement>(null);
  const tabsSections = useRef<HTMLDivElement>(null);

  const activeTab = activeIndex ?? 0;
  useSectionsHeightAnimation(
    400,
    activeTab,
    tabsSections,
    autoScrollOffset,
    disableAutoScroll,
  );
  useTabGlider(tabTitles, activeTab);

  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));

  const toggleAccordion = (index: number) => {
    if (index === activeIndex) {
      setActiveIndex(null);
    } else {
      setActiveIndex(index);
    }
  };

  return isMobile && !showTabsOnMobile && !showDropdownOnMobile ? (
    <MobileContainer className={className}>
      <Grid size={12}>
        {sections.map((section, idx) => (
          <StyledAccordion
            key={idx}
            expanded={idx === activeIndex}
            onChange={() => toggleAccordion(idx)}
            backgroundColor={backgroundColor}
          >
            <AccordionSummary>{section.title}</AccordionSummary>
            <StyledAccordionDetails>{section.content}</StyledAccordionDetails>
          </StyledAccordion>
        ))}
      </Grid>
    </MobileContainer>
  ) : (
    <DesktopContainer className={className}>
      <ResponsiveGrid>
        <Grid size={12}>
          {isMobile && showDropdownOnMobile && sections.length > 3 ? (
            <MobileDropdown
              activeIndex={activeIndex ?? 0}
              onChange={(index) => setActiveIndex(index)}
              items={sections.map((section) => ({ name: section.title }))}
              hideBorderBottom={true}
            />
          ) : (
            <div
              style={{
                width: 'fit-content',
                margin: alignTabs === 'center' ? '0 auto' : 0,
                maxWidth: '100%',
              }}
            >
              <TabTitles
                ref={tabTitles}
                onChange={(_, value) => setActiveIndex(value)}
                value={activeTab}
                variant="scrollable"
                scrollButtons="auto"
                slots={{
                  StartScrollButtonIcon: StartIcon,
                  EndScrollButtonIcon: EndIcon,
                }}
                TabScrollButtonProps={{ disableRipple: true }}
                TabIndicatorProps={{ sx: { display: 'none' } }}
              >
                {sections.map((section, idx) => (
                  <TabTitle
                    key={idx}
                    id={`tab-${idx}`}
                    role="tab"
                    aria-controls={`tabpanel-${idx}`}
                    aria-selected={idx === activeTab}
                    label={section.title}
                    disableRipple
                  />
                ))}
              </TabTitles>
            </div>
          )}
        </Grid>
      </ResponsiveGrid>
      <ResponsiveGrid>
        <SectionWrapper
          size={12}
          ref={tabsSections}
          paddingBetweenTitelAndContent={paddingBetweenTitelAndContent}
        >
          {sections.map((section, idx) => (
            <section
              key={idx}
              id={`tabpanel-${idx}`}
              role="tabpanel"
              aria-labelledby={`tab-${idx}`}
              style={{
                position: 'relative',
                zIndex: 0,
                pointerEvents: activeTab === idx ? 'auto' : 'none',
              }}
            >
              {section.content}
            </section>
          ))}
        </SectionWrapper>
      </ResponsiveGrid>
    </DesktopContainer>
  );
};

const DesktopContainer = styled('div')(({ theme }) => ({
  marginTop: '60px',
  marginBottom: '70px',

  [theme.breakpoints.up('md')]: {
    marginTop: '70px',
    marginBottom: '80px',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '80px',
    marginBottom: '100px',
  },
}));

const MobileContainer = styled(ResponsiveGrid)(({ theme }) => ({
  marginTop: '60px',
  marginBottom: '70px',
}));

const MobileDropdown = styled(TextSelect)(({ theme }) => ({
  width: '100%',

  [`& .${inputBaseClasses.root}`]: {
    width: '100%',
  },
}));

const TabTitles = styled(MuiTabs)(({ theme }) => ({
  [`& .${tabsClasses.flexContainer}`]: {
    margin: '0 auto',
    columnGap: 'var(--spacer-l)',
    alignItems: 'flex-end',
    minHeight: 'calc(calc(var(--font-size-h4) * var(--line-height-h4)) + 15px)',

    '&:after': {
      content: "''",
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '2px',
      scale: 'var(--_width, 0) 1',
      translate: 'var(--_left, 0) 0',
      transformOrigin: 'left',
      transition: 'scale 200ms, translate var(--transition-duration)',
      backgroundColor: 'var(--color-light-red)',
      display: 'var(--_show, none)',

      [theme.breakpoints.up('sm')]: {
        height: '3px',
      },

      [theme.breakpoints.up('md')]: {
        height: '4px',
      },

      [theme.breakpoints.up('lg')]: {
        height: '5px',
      },
    },
  },

  [`&.${tabsClasses.root} .${tabScrollButtonClasses.root}`]: {
    alignSelf: 'center',
    height:
      'calc(var(--real-tabs-height) - var(--height-diff-selected) - var(--shadow-spacing))',
    color: 'var(--color-dark-coal)',
    width: 'unset',
    marginTop: '0',
    backgroundColor: `transparent !important`,

    '&:first-of-type': {
      marginRight: 'var(--spacer-s)',
    },

    '&:last-of-type': {
      marginLeft: 'var(--spacer-s)',
    },
    opacity: 1,

    svg: {
      width: '36px',
      height: '36px',
    },

    [theme.breakpoints.up('md')]: {
      svg: {
        width: '48px',
        height: '48px',
      },
    },

    [`&.${tabScrollButtonClasses.disabled}`]: {
      opacity: '0',
    },
  },
}));

const TabTitle = styled(MuiTab)(({ theme }) => ({
  [`&.${buttonBaseClasses.root}.${tabClasses.root}`]: {
    ...theme.typography.h6,
    backgroundColor: 'transparent',
    border: 'none',
    textWrap: 'nowrap',
    padding: 0,
    paddingBottom: '9px',
    color: 'var(--color-dark-coal)',
    minWidth: 'unset',
    maxWidth: 'unset',
    minHeight: 'unset',
    textTransform: 'unset',

    [theme.breakpoints.up('sm')]: {
      paddingBottom: '10px',
    },

    [theme.breakpoints.up('md')]: {
      paddingBottom: '12px',
    },

    [theme.breakpoints.up('lg')]: {
      paddingBottom: '15px',
    },

    '&:hover': {
      color: 'var(--color-light-red)',
      cursor: 'pointer',
    },

    [`&.${tabClasses.selected}`]: {
      ...theme.typography.h4,
      color: 'var(--color-light-red)',
    },
  },
}));

const SectionWrapper = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'paddingBetweenTitelAndContent',
})<{ paddingBetweenTitelAndContent?: string }>(
  ({ theme, paddingBetweenTitelAndContent }) => ({
    paddingTop: paddingBetweenTitelAndContent ?? 'var(--spacer-l)',

    ...(paddingBetweenTitelAndContent === undefined
      ? {
          [theme.breakpoints.up('sm')]: {
            paddingTop: '35px',
          },

          [theme.breakpoints.up('md')]: {
            paddingTop: '50px',
          },

          [theme.breakpoints.up('lg')]: {
            paddingTop: '60px',
          },
        }
      : undefined),
  }),
);

const StyledAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{
  backgroundColor: CSSProperties['backgroundColor'];
}>(({ theme, backgroundColor }) => ({
  [`&.${paperClasses.root}.${accordionClasses.root}:first-of-type > .${accordionSummaryClasses.root}`]:
    {
      borderTop: 'none',
    },
  [`&.${paperClasses.root}.${accordionClasses.root}`]: {
    backgroundColor,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)({
  [`&.${accordionDetailsClasses.root}`]: {
    paddingBottom: '34px',
    paddingTop: 0,
  },
});
